import React, {
  useState, forwardRef, useCallback,
} from 'react';
import ContentSection from '@/pages/Products/VpsOciDetailPage/commons/ContentSection';
import useLocale from '@/hooks/useLocale';
import * as Styles from './MyPlan.styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button, IconRefresh, Skeleton, Tag,
} from 'gatorcomponents';
import { useResize } from '@/hooks/useResize';
import { vpsOciActions } from '@/redux/modules';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useParams } from 'react-router-dom';
import { useVpsStatus } from '@/hooks/useVpsStatus';
import { billingAnalytics } from '@/analytics';

import { ENABLE_VPS_OCI_DETAIL_PAGE_STATUS } from '@/config/GrowthBook/constants';

import RebootModal from './components/RebootModal';
import RebootHistoryModal from './components/RebootHistoryModal';

const MyPlan = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const [showRebootModal, setShowRebootModal] = useState(false);
  const [showRebootHistoryModal, setShowRebootHistoryModal] = useState(false);
  const isVpsOCIStatusEnabled = useFeatureIsOn(ENABLE_VPS_OCI_DETAIL_PAGE_STATUS);
  const { hostingAndServers: hostingAndServersAnalytics } = billingAnalytics;
  const vpsOci = useSelector(state => state.vpsOci);
  const { vps } = (vpsOci && vpsOci.data) || {};

  const { id: hostingId } = useParams();

  const {
    status,
    statusData,
    loadingStatus,
    isRebooting,
    isRebuilding,
    isDisabledButton,
  } = useVpsStatus(hostingId);

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: locale } = productDetailsLocale;

  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width < 600;

  const toggleModals = (modalToShow) => {
    setShowRebootModal(modalToShow === 'reboot');
    setShowRebootHistoryModal(modalToShow === 'rebootHistory');
  };

  const handleCloseRebootModal = () => {
    setShowRebootModal(false);
  };

  const handleRebootConfirm = useCallback(() => {
    if (!hostingId) return;

    dispatch(vpsOciActions.reboot.request(hostingId));

    const productName = vpsOci?.data?.productName;
    hostingAndServersAnalytics.hostingMyPlanRebootClick(productName);
  }, [dispatch, hostingId, hostingAndServersAnalytics, vpsOci]);

  if (!vpsOci || !vps) return null;

  return (
    <>
      <ContentSection ref={ref} direction="row" align="center" justify="space-between" testId="vpsoci-myplan-section">
        <Styles.InfoWrapper>
          <Styles.Title data-testid="vpsoci-myplan-title">{locale.myPlan.title}</Styles.Title>
          <Styles.PlanInfo>
            <Styles.ProductName data-testid="vpsoci-myplan-product-name">{vpsOci.data.productName}</Styles.ProductName>
            {(() => {
              if (!isVpsOCIStatusEnabled) return null;
              if (loadingStatus && !isRebooting && !isRebuilding) return <Skeleton width="100px" height="28px" />;
              if (status) return <Tag label={status.value} variant={status.variant} size="md" testId={`vpsoci-myplan-status-tag-${statusData}`} />;
              return null;
            })()}
          </Styles.PlanInfo>
        </Styles.InfoWrapper>
        <Styles.ButtonWrapper>
          <Button
            label={locale.myPlan.reboot}
            variant="secondary"
            size="medium2"
            iconLeft={!isRebooting && <IconRefresh color={isDisabledButton ? 'lowLight' : 'primaryMedium'} />}
            notFillSVG
            width={isMobile ? 'full' : 'fit-content'}
            onClick={!isDisabledButton ? () => setShowRebootModal(true) : undefined}
            disabled={isDisabledButton}
            loading={isRebooting}
            testId="my-plan-reboot-button"
          />
        </Styles.ButtonWrapper>
      </ContentSection>
      {showRebootModal && (
        <RebootModal
          onClose={handleCloseRebootModal}
          onConfirm={handleRebootConfirm}
          onCheckRebootHistory={() => toggleModals('rebootHistory')}
        />
      )}
      {showRebootHistoryModal && (
        <RebootHistoryModal
          onClose={() => setShowRebootHistoryModal(false)}
          onGoBack={() => toggleModals('reboot')}
        />
      )}
    </>
  );
});

MyPlan.displayName = 'MyPlan';

export default MyPlan;
